// features/auth/AuthRecord.tsx — the public record, typeset beside the sign-in form.
//
// The one thing this product has that no other sign-up page can carry is its
// own output: a verdict, with the written reasons and the vote. So that is what
// sits beside the form, set as a document rather than as a panel of cards. The
// copy is the landing page's curated pair, imported from one module so the two
// surfaces cannot drift. Nothing here needs the network at first paint.
//
// The page ground carries the Register ruling. Every block that holds text
// wears the `textBlock` mixin: its own bgRoot fill hides the ruling under the
// glyphs and feathers out over 24px, so the lines are fully visible in the
// margins and the gutter and never under a word. Same idea as the shell
// backdrop's gutter mask, applied per block.

import { css } from 'styled-components';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { query } from '@/theme/breakpoints';
import {
  OUTCOME_LABEL,
  VERDICT_EXAMPLES,
  VERDICTS_HEADING,
  VERDICTS_INTRO,
  VERDICTS_RECEIPTS_AFTER,
  VERDICTS_RECEIPTS_BEFORE,
  VERDICTS_RECEIPTS_LINK,
} from '@/features/landing/sections/verdictsCopy';

export const textBlock = css`
  background: ${({ theme }) => theme.color.bgRoot};
  padding-inline: 48px;
  margin-inline: -48px;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
  @media ${query.tabletDown} {
    padding-inline: 0;
    margin-inline: 0;
    -webkit-mask-image: none;
    mask-image: none;
    background: none;
  }
`;

const Head = styled.div`
  grid-area: head;
  margin-bottom: 32px;
  ${textBlock}
  @media ${query.tabletDown} {
    margin: 0;
    padding: 40px 40px 24px;
    border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
  @media ${query.mobile} {
    padding: 24px 20px 20px;
  }
`;

const Heading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.color.textPrimary};
  @media ${query.tabletDown} {
    font-size: ${({ theme }) => theme.fontSize.md};
    line-height: 24px;
  }
`;

const Intro = styled.p`
  margin: 8px 0 0;
  text-wrap: pretty;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 20px;
  color: ${({ theme }) => theme.color.textMuted};
  @media ${query.tabletDown} {
    margin-top: 4px;
  }
`;

const Body = styled.div`
  grid-area: body;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  @media ${query.tabletDown} {
    padding: 32px 40px 40px;
    border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
  @media ${query.mobile} {
    padding: 32px 20px;
  }
`;

const Text = styled.div`
  ${textBlock}
  margin-bottom: 48px;
  @media ${query.tabletDown} {
    margin-bottom: 0;
  }
`;

const Entries = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/* One entry, set the way the Council's own record lists a case: the outcome
   word in the margin, the reasons beside it, a hairline under each. No colour,
   no icon, no pill — the record is ink. */
const Entry = styled.li`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  column-gap: 24px;
  align-items: start;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  @media ${query.tabletDown} {
    display: block;
  }
`;

const Outcome = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  line-height: 24px;
  color: ${({ theme }) => theme.color.textPrimary};
  @media ${query.tabletDown} {
    display: inline;
    margin-right: 6px;
  }
`;

const Reasons = styled.p`
  margin: 0;
  /* Keeps the vote ("Kept, 6–1.") off a line of its own; progressive, ignored where unsupported. */
  text-wrap: pretty;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.font.bodyWeight};
  line-height: 24px;
  color: ${({ theme }) => theme.color.textPrimary};
  @media ${query.tabletDown} {
    display: inline;
  }
`;

const Category = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 24px;
  color: ${({ theme }) => theme.color.textMuted};
  @media ${query.tabletDown} {
    display: block;
    margin-top: 4px;
  }
`;

const Receipts = styled.p`
  margin: 20px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 20px;
  color: ${({ theme }) => theme.color.textMuted};
  a {
    color: ${({ theme }) => theme.color.accentCyan};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Foot = styled.div`
  ${textBlock}
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media ${query.tabletDown} {
    margin-top: 32px;
  }
`;

const FootLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px 10px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 20px;
  color: ${({ theme }) => theme.color.textFaint};
  span {
    white-space: nowrap;
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

export function RecordHead() {
  return (
    <Head>
      <Heading id="auth-record-heading">{VERDICTS_HEADING}</Heading>
      <Intro>{VERDICTS_INTRO}</Intro>
    </Head>
  );
}

export function RecordBody() {
  return (
    <Body>
      <Text>
        <Entries aria-labelledby="auth-record-heading">
          {VERDICT_EXAMPLES.map((v) => (
            <Entry key={v.category}>
              <Outcome>{OUTCOME_LABEL[v.outcome]}</Outcome>
              <div>
                <Reasons>{v.reasons}</Reasons>
                <Category>{v.category}</Category>
              </div>
            </Entry>
          ))}
        </Entries>
        <Receipts>
          {VERDICTS_RECEIPTS_BEFORE}
          <Link to="/council">{VERDICTS_RECEIPTS_LINK}</Link>
          {VERDICTS_RECEIPTS_AFTER}
        </Receipts>
      </Text>
      <Foot>
        {/* The three phrases the card used to carry as a "trust row". The
            design review proposes deleting them (landing-page filler, and the
            record beside them makes the same claims in evidence). That is the
            owner's call, so they stay until it is made — quietly, in the foot. */}
        <FootLine className="features">
          <span>Secure &amp; Private</span>
          <span>Freedom of Expression</span>
          <span>Global Community</span>
        </FootLine>
        {/* Moved here from the card's footer. The two links used to be href="#",
            a control that went nowhere; they now open the documents the sign-up
            checkbox already links to. */}
        <FootLine className="legal">
          <span>© 2026 TruXSocial</span>
          <a href="/policies/terms">Terms of Service</a>
          <a href="/policies/privacy">Privacy Policy</a>
        </FootLine>
      </Foot>
    </Body>
  );
}
